import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls =
  "w-full bg-paper border border-ink/15 focus:border-alta outline-none px-4 py-3 text-sm font-light text-ink transition-colors";

const FIELDS = [
  { key: "hero_image", label: "Hero image URL", type: "image" },
  { key: "hero_kicker", label: "Hero kicker" },
  { key: "hero_line1", label: "Hero headline — line 1" },
  { key: "hero_line2", label: "Hero headline — line 2" },
  { key: "hero_sub", label: "Hero supporting copy", type: "textarea" },
  { key: "intro_line1", label: "Intro headline — line 1" },
  { key: "intro_line2", label: "Intro headline — line 2" },
  { key: "intro_text", label: "Intro paragraph", type: "textarea" },
  { key: "intro_image", label: "Intro image URL", type: "image" },
  { key: "quote_text", label: "Philosophy quote", type: "textarea" },
  { key: "quote_author", label: "Quote attribution" },
  { key: "about_preview_title", label: "About preview title" },
  { key: "about_preview_text", label: "About preview copy", type: "textarea" },
  { key: "about_preview_image", label: "About preview image URL", type: "image" },
  { key: "about_hero_image", label: "About page hero image URL", type: "image" },
  { key: "editorial_label", label: "Editorial label" },
  { key: "editorial_title", label: "Editorial title" },
  { key: "editorial_text", label: "Editorial copy", type: "textarea" },
  { key: "editorial_image", label: "Editorial image URL", type: "image" },
  { key: "editorial_link", label: "Editorial link (e.g. /journal/the-woman-in-red)" },
  { key: "work_categories", label: "Work categories (one per line)", type: "lines" },
  { key: "studio_images", label: "Studio feed image URLs (one per line)", type: "lines" },
  { key: "social_instagram", label: "Instagram URL" },
  { key: "social_pinterest", label: "Pinterest URL" },
  { key: "social_email", label: "Contact email" },
  { key: "contact_note", label: "Contact page note", type: "textarea" },
  { key: "footer_note", label: "Footer message" },
];

export default function ContentAdmin() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setValues(r.data)).catch(() => toast.error("Could not load content"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { values });
      toast.success("Site content updated");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!values) {
    return <p className="text-ink/50 font-light text-sm" data-testid="content-loading">Loading content…</p>;
  }

  const set = (key, v) => setValues((s) => ({ ...s, [key]: v }));

  return (
    <div data-testid="content-admin">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-light text-ink">Site Content</h1>
          <p className="mt-2 text-sm font-light text-ink/55">Homepage copy, imagery, social links and notes.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-button"
          className="bg-alta text-paper px-8 py-3 text-[10px] font-medium tracking-[0.22em] uppercase transition-colors duration-500 hover:bg-ink disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save All"}
        </button>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-8">
        {FIELDS.map((f) => (
          <div key={f.key} className={f.type === "textarea" || f.type === "lines" ? "lg:col-span-2" : ""}>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-ink/50 mb-2">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea rows={3} value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className={`${inputCls} resize-y`} data-testid={`content-field-${f.key}`} />
            ) : f.type === "lines" ? (
              <textarea
                rows={4}
                value={(values[f.key] || []).join("\n")}
                onChange={(e) => set(f.key, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                className={`${inputCls} resize-y`}
                data-testid={`content-field-${f.key}`}
              />
            ) : (
              <div className="space-y-2">
                <input value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className={inputCls} data-testid={`content-field-${f.key}`} />
                {f.type === "image" && values[f.key] && (
                  <img src={values[f.key]} alt="preview" className="h-20 object-cover border border-ink/10" />
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
